import { Link, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { getNewsById, getRelatedNews, formatDate } from "@/utils/media";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;
  const related = item ? getRelatedNews(item, 4) : [];

  useSEO({
    title: item ? `${item.title} - 麻豆精品` : "影视资讯 - 麻豆精品",
    description: item ? item.summary : "麻豆精品影视资讯详情页。",
    keywords: item ? `麻豆精品,${item.tags.join(",")},影视资讯` : "麻豆精品,影视资讯",
  });

  if (!item) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-newspaper-line"
          title="未找到该资讯"
          description="链接可能已失效，或该资讯暂未收录。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[900px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: item.category }]} />

      <article className="mt-5">
        <span className="inline-block rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
          {item.category}
        </span>
        <h1 className="mt-4 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
          {item.title}
        </h1>

        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" aria-hidden="true"></i>
            {item.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            发布于 {formatDate(item.publishedAt)}
          </span>
        </div>

        <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 麻豆精品`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <p className="mt-6 rounded-lg border-l-4 border-accent-500 bg-background-50 p-4 text-sm font-medium leading-relaxed text-foreground-700 md:text-base">
          {item.summary}
        </p>

        <div className="mt-6 space-y-5 text-sm leading-loose text-foreground-700 md:text-base">
          {item.content.map((paragraph) => (
            <p key={paragraph.slice(0, 16)}>{paragraph}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
          <span className="text-sm text-foreground-500">相关标签：</span>
          {item.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
            >
              #{tag}
            </span>
          ))}
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950">
            <span className="h-2 w-2 rounded-full bg-accent-500"></span>
            相关资讯
          </h2>
          <ul className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {related.map((news) => (
              <li key={news.id}>
                <Link
                  to={`/news/${news.id}`}
                  className="group flex items-start gap-4 p-4 transition-colors hover:bg-background-100 md:p-5"
                >
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-20 md:w-32">
                    <img
                      src={news.cover}
                      alt={news.title}
                      title={`${news.title} - 麻豆精品`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-medium text-accent-700">{news.category}</span>
                    <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                      {news.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-400">
                      {formatDate(news.publishedAt)}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-10 flex justify-center">
        <Link
          to="/news"
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:bg-primary-50"
        >
          <i className="ri-arrow-left-line" aria-hidden="true"></i>
          返回资讯列表
        </Link>
      </div>
    </div>
  );
}